import { Alert, Button, Checkbox, Col, Collapse, Form, FormInstance, Input, message, Row, Space, Tag } from "antd";
import { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { UploadFile } from "antd/es/upload/interface";
import debounce from "lodash.debounce";
import { DebouncedFunc } from 'lodash'
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../../../assets/icons/icon";
import CustomCollapse from "../../../components/customCollapse";
import DocumentsUpload from "../../../components/documentsUpload";
import DateSelect from "../../../components/form/dateSelect";
import EnumSelect from "../../../components/form/enumSelect";
import InputNum from "../../../components/form/inputNum";
import DividerLine from "../../../components/lineSplit";
import LoadingContainer from "../../../components/loadingContainer";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import openModal from "../../../components/modal/openModal";
import { CSVField } from "../../../components/modal/taxCaseBulkCSVUpload";
import UserStore from "../../../contexts/userStore";
import _t from "../../../lang/translate";
import { getAboutUser, getCreditCount } from "../../../services/authService";
import { scrapeLink } from "../../../services/miscService";
import { taxCaseBulkCreation, taxFileUrl } from "../../../services/taxService";
import { getRemoteData, patchVehicle } from "../../../services/vehicleService";
import { Client, RemoteVehicleData, ScrapedVehicleType } from "../../../types/appTypes";
import { TaxCaseCreationBulk } from "../../../types/taxTypes";
import { convertToApiFileList } from "../../../utilities/convertTypes";
import { isValidLink } from "../../../utilities/readLink";
import { isValidVin } from "../../../utilities/stringExtraFunctions";
import TaxNewCaseAdminPanel from "../taxCommon/admin/taxNewCaseAdminPanel";
import openAsyncModal from "../motorAtlasCatalogMapper/openAsyncModal";
import { ModelCatalogFilterForm, ModelMatch } from "../../../types/motorAtlasCatalogTypes";
import CustomCollapseHeader from "../../../components/customCollapseHeader";
import { AxiosResponse } from "axios";
import { isValidNum } from "../../../utilities/typeGuard";


function mapToBulkCreationPayload(values: NewTaxCaseFormType): TaxCaseCreationBulk {
  const entries = values.vehicles.map<TaxCaseCreationBulk['entries'][number]>((entry) => {
    // When link is present, but vin is not
    // It's because you have "dataSourceId": 2 which indicates vin
    let dataSourceId = 2 // vin
    if (isValidLink(entry.link)) {
      dataSourceId = 1 // link
    }
    if (entry.motorAtlasId) {
      dataSourceId = 4 // motorAtlasId / eCode
    }

    return {
      evaluationTypeId: entry.evaluationTypeId ?? -1,
      files: entry.files ? convertToApiFileList(entry?.files) : null,
      flags: {
        isPriority: entry.isPriority ?? false
      },
      note: entry.note ?? null,
      dataSourceId: dataSourceId,
      vehicle: {
        firstRegDate: entry.firstRegDate,
        mileage: entry.mileage,
        useCaseId: entry.useCaseId,
        vin: entry.vin,
        link: entry.link,
        motorAtlasId: entry.motorAtlasId
      }
    }
  })

  return {
    calculationTypeId: values.calculationTypeId,
    client: values.client as Client,
    entries: entries,
    paymentMethodId: values.paymentMethodId,
    reason: values.reason
  }

}

type NewTaxCaseFormType = {
  vehicles: {
    input: string | null | undefined;
    vin: string | null | undefined;
    link: string | null | undefined;
    motorAtlasId: string | null | undefined;
    firstRegDate: string | null | undefined;
    mileage: number | null | undefined;
    useCaseId: number | null | undefined;
    evaluationTypeId: number | null | undefined;
    note: string | null | undefined;
    isPriority: boolean | null | undefined;
    files: UploadFile[] | null | undefined;
  }[]
  calculationTypeId: number | null | undefined;
  paymentMethodId: number | null | undefined;
  reason: string | null | undefined;
  client: Partial<Client> | null;
}


const defaultVehicleValue: NewTaxCaseFormType['vehicles'][number] = {
  evaluationTypeId: 1,
  firstRegDate: undefined,
  isPriority: false,
  mileage: undefined,
  note: undefined,
  input: undefined,
  useCaseId: undefined,
  files: undefined,
  link: undefined,
  vin: undefined,
  motorAtlasId: undefined
}

function calculateClipCost(values: NewTaxCaseFormType | CSVField[]) {
  let clipCost = 0;
  if (Array.isArray(values)) {
    values.forEach(item => {
      if (normalize(item.taxType) === 'gul til hvid') {
        clipCost += 2;
      } else {
        clipCost += 1
      }
    })
  } else {
    values.vehicles.forEach(item => {
      // evaluationTypeId: 3 yellow-to-white is 2 clips (credits), while van or private is only 1 cost.
      clipCost = clipCost + (item.evaluationTypeId === 3 ? 2 : 1)
    })
  }
  return clipCost
}

type Props = {
  refreshData: () => void;
}
export function NewTaxCase({ refreshData }: Props) {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<NewTaxCaseFormType>()
  const { user } = UserStore.useContainer()
  const [clipCost, setClipCost] = useState(1)
  const [activeKeys, setActiveKeys] = useState<Set<number>>(new Set([0]))
  const clips = getCreditCount()
  const notEnoughClips = clipCost > clips

  const defaultFormValues: NewTaxCaseFormType = {
    vehicles: [defaultVehicleValue],
    client: {
      userId: user?.id ?? -1,
      dealerId: user?.dealer?.id ?? -1,
      typeId: 1,
    },
    calculationTypeId: 1,
    paymentMethodId: 1,
    reason: undefined
  }


  async function handleSubmit(values: NewTaxCaseFormType) {
    try {
      setLoading(true)
      if (clipCost > clips) {
        throw new Error(_t("not_enough_credits"))
      }
      const payload = mapToBulkCreationPayload(values)
      const { data: { data } } = await taxCaseBulkCreation(payload)
      form.resetFields()
      setClipCost(1)
      if (typeof data === 'string') {
        message.success(data)
        getAboutUser()
      } else if (data === true) {
        message.success(`${values.vehicles.length} sager oprettet!`)
        // TODO: When should this be automatically called?
        refreshData()

      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFinishFailed = (errorInfo: any) => {
    const errorFields = errorInfo?.errorFields
    const vehicleErrors: any[] = errorFields.filter((item: any) => item?.name?.[0] === 'vehicles')
    vehicleErrors.forEach(item => {
      const [_, fieldKey] = item.name
      if (isValidNum(fieldKey)) {
        setActiveKeys((prev) => new Set(prev).add(fieldKey))
      }
    })
  }

  function handleValuesChange(changed: Partial<NewTaxCaseFormType>, values: NewTaxCaseFormType) {
    if (changed.vehicles) {
      setClipCost(calculateClipCost(values))
    }
  }

  return (
    <LoadingContainer showLogo loading={loading}>
      <Form form={form}
        layout="vertical"
        initialValues={defaultFormValues}
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        onFinishFailed={handleFinishFailed}
      >
        <CustomCollapse className="ml-0 mr-0">
          <Collapse.Panel key="1" header={
            <CustomCollapseHeader>
              <Space align="center">
                <Icon name="user-list-outline" />
                <span>Opret sag på vegne af</span>
              </Space>
            </CustomCollapseHeader>
          } forceRender>
            <TaxNewCaseAdminPanel hideSubmitBtn hideCard loading={loading} />
          </Collapse.Panel>
        </CustomCollapse>
        <Form.List name="vehicles">
          {(fields, actions, { errors }) => (
            <ManageNewTaxCaseForms
              activeKeys={activeKeys}
              setActiveKeys={setActiveKeys}
              setClipCost={setClipCost}
              form={form}
              fields={fields}
              actions={actions}
              errors={errors}
            />
          )}
        </Form.List>
        <DividerLine fluid />
        <Row gutter={[8, 8]} align="middle" justify="end">
          <Col>
            <ClipCost clipCost={clipCost} />
          </Col>
          <Col><Button disabled={notEnoughClips} htmlType="submit" type="primary">Opret sag ({clipCost})</Button></Col>
        </Row>
      </Form>
    </LoadingContainer>
  )
}

type FormListItemConfig = {
  vinVehicle: RemoteVehicleData | null;
  linkVehicle: ScrapedVehicleType | null;
  sourceType: SourceType | null
  loadingSourceData: boolean;
  mileageDisabled: boolean;
  firstRegDateDisabled: boolean;
}
function normalize(str: string | undefined) {
  return str?.toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[-]*/g, " ")
    .trim()
}
type ManageNewTaxCaseFormsProps = {
  form: FormInstance<NewTaxCaseFormType>;
  fields: FormListFieldData[];
  actions: FormListOperation
  errors: React.ReactNode[];
  setClipCost: React.Dispatch<React.SetStateAction<number>>
  activeKeys: Set<number>;
  setActiveKeys: React.Dispatch<React.SetStateAction<Set<number>>>
}
function ManageNewTaxCaseForms({ form, actions, fields, errors, setClipCost, activeKeys, setActiveKeys }: ManageNewTaxCaseFormsProps) {
  const { add, move, remove } = actions
  const [config, setConfig] = useState(new Map<FormListFieldData['key'], FormListItemConfig>())
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  function addVehicle() {
    add(defaultVehicleValue)
    setClipCost(prev => prev + 1)
  }
  function handleDelete(field: FormListFieldData) {
    remove(field.name)
    setClipCost(prev => prev - 1)
  }
  function handleSourceDataChange(val: string | null | undefined, field: FormListFieldData) {
    if (isValidLink(val)) {
      setSourceType("link", field)
      debouncedFetchLink(val, field)
      form.setFields([{ name: ["vehicles", field.name, 'link'], value: val }])
    }
    else if (isValidVin(val)) {
      setSourceType("vin", field)
      debouncedFetchVin(val, field)
      form.setFields([{ name: ["vehicles", field.name, 'vin'], value: val }])
      setLinkVehicle(null, field)
    }
  }

  const fetchVinData = async (vin: string, field: FormListFieldData) => {
    try {
      if (!isValidVin(vin)) {
        setVinVehicle(null, field)
        return;
      };
      setLoadingSourceData(true, field)
      const { data } = await getRemoteData({ items: ['dmr'], vin: vin });
      setVinVehicle(data.data.dmr ? data.data : null, field)
      const firstRegDate = data.data?.dmr?.mapped?.firstRegDate ?? config.get(field.key)?.linkVehicle?.vehicle?.firstRegDate;

      setMileageDisabled(false, field)
      setFirstRegDateDisabled(!!firstRegDate, field)
      if (firstRegDate) {
        form.setFields([
          {
            name: ['vehicles', field.name, "firstRegDate"],
            value: firstRegDate
          }
        ])
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingSourceData(false, field)
    }
  }

  const debouncedFetchVin = useCallback(
    debounce((vin: string, field: FormListFieldData) => {
      fetchVinData(vin, field)
    }, 200), [fetchVinData]
  );

  const fetchLinkData = async (link: string, field: FormListFieldData) => {
    try {
      if (!isValidLink(link)) {
        setLinkVehicle(null, field)
        return
      };
      setLoadingSourceData(true, field)
      const { data } = await scrapeLink(link);
      setLinkVehicle(data.data, field)

      const mileage = data?.data?.vehicle?.mileage
      const firstRegDate = config.get(field.key)?.vinVehicle?.dmr?.mapped?.firstRegDate ?? data?.data?.vehicle?.firstRegDate

      setMileageDisabled(!!mileage, field)
      setFirstRegDateDisabled(!!firstRegDate, field)
      form.setFields([
        {
          name: ['vehicles', field.name, "firstRegDate"],
          value: firstRegDate
        },
        {
          name: ['vehicles', field.name, "mileage"],
          value: mileage
        }
      ])
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingSourceData(false, field)
    }
  }
  const debouncedFetchLink = useCallback(
    debounce((link: string, field: FormListFieldData) => {
      fetchLinkData(link, field)
    }, 200), [fetchLinkData]
  );

  async function bulkCreateViaCSV() {
    try {
      const data = await openModal<CSVField[]>("taxCaseBulkCSVUpload", {})
      const callbacks: [Function, object, CSVField][] = []

      form.setFieldsValue({
        vehicles: data.map(row => {
          let evaluationTypeId = 1;
          if (normalize(row?.taxType) === 'personbil') {
            evaluationTypeId = 1
          }
          if (normalize(row?.taxType) === 'van' || normalize(row?.taxType) === 'varebil') {
            evaluationTypeId = 2;
          }
          if (normalize(row?.taxType) === 'gul til hvid') {
            evaluationTypeId = 3;
          }

          if (isValidVin(row.vinOrLink)) {
            callbacks.push([getRemoteData, { items: ['dmr'], vin: row.vinOrLink }, row])
          } else if (isValidLink((row.vinOrLink))) {
            callbacks.push([scrapeLink, row.vinOrLink, row])
          }

          return {
            evaluationTypeId: evaluationTypeId,
            firstRegDate: row.firstRegDate,
            isPriority: false,
            mileage: Number(row.mileage),
            note: undefined,
            input: row.vinOrLink,
            vin: isValidVin(row.vinOrLink) ? row.vinOrLink : null,
            link: isValidLink(row.vinOrLink) ? row.vinOrLink : null,
            useCaseId: 1
          }
        })
      })

      setClipCost(calculateClipCost(data))
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('special-event-to-handle-async-vehicle-fetching', { detail: callbacks }))
      }, 1)

    } catch (error) {
      renderErrorMessage(error)
    }
  }
  type CBEntry =
    | [fn: typeof scrapeLink, arg: Parameters<typeof scrapeLink>[0], row: CSVField]
    | [fn: typeof getRemoteData, arg: Parameters<typeof getRemoteData>[0], row: CSVField];
  /** Dont ask. Dont tell. ignore this. Never touch this garbage again...*/
  useEffect(() => {
    const listener = (e: Event) => {
      const callbacks = (e as CustomEvent<CBEntry[]>).detail;
      const byInput = new Map<string, typeof fieldsRef.current[number]>();
      for (const field of fieldsRef.current) {
        const inp = form.getFieldValue(['vehicles', field.name, 'input']);
        if (inp) byInput.set(String(inp), field);
      }
      for (const [fn, arg, row] of callbacks) {
        const field = byInput.get(row.vinOrLink ?? '');
        if (!field) continue;
        setLoadingSourceData(true, field);

        Promise.resolve()
          // @ts-ignore
          .then(() => fn(arg as any))
          .then((res) => {
            const data = res.data.data as RemoteVehicleData | ScrapedVehicleType;
            const userFirstRegDate = row.firstRegDate;
            const userMileage = row.mileage;
            if (!data) return;
            if ('dmr' in data) {
              // VIN branch
              setVinVehicle(data, field);
              setSourceType('vin', field);
              if (!userFirstRegDate) {
                form.setFields([{ name: ['vehicles', field.name, 'firstRegDate'], value: data.dmr?.mapped?.firstRegDate }]);
              }
              if (!userMileage) {
                form.setFields([{ name: ['vehicles', field.name, 'mileage'], value: data.dmr?.mapped?.mileage }]);
              }
            } else {
              // Link branch
              setLinkVehicle(data, field);
              setSourceType('link', field);
              if (!userFirstRegDate) {
                form.setFields([{ name: ['vehicles', field.name, 'firstRegDate'], value: data?.vehicle?.firstRegDate }]);
              }
              if (!userMileage) {
                form.setFields([{ name: ['vehicles', field.name, 'mileage'], value: data?.vehicle?.mileage }]);
              }
            }
          })
          .catch(renderErrorMessage)
          .finally(() => {
            setLoadingSourceData(false, field);
          });
      }
    }
    window.addEventListener('special-event-to-handle-async-vehicle-fetching', listener)
    return () => window.removeEventListener('special-event-to-handle-async-vehicle-fetching', listener)
  }, [])


  function setSourceType(sourceType: SourceType | null, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          sourceType,
        })
      }
      return map;
    })
  }
  function setLinkVehicle(linkVehicle: ScrapedVehicleType | null, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          linkVehicle,
        })
      }
      return map;
    })
  }
  function setVinVehicle(vinVehicle: RemoteVehicleData | null, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          vinVehicle,
        })
      }
      return map;
    })
  }
  function setLoadingSourceData(loadingSourceData: boolean, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          loadingSourceData,
        })
      }
      return map;
    })
  }
  function setMileageDisabled(mileageDisabled: boolean, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          mileageDisabled,
        })
      }
      return map;
    })
  }
  function setFirstRegDateDisabled(firstRegDateDisabled: boolean, field: FormListFieldData) {
    setConfig(prev => {
      const map = new Map(prev)
      const old = map.get(field.key)
      if (old) {
        map.set(field.key, {
          ...old,
          firstRegDateDisabled,
        })
      }
      return map;
    })
  }

  return (
    <>
      {fields.map(field => (
        <Row wrap={false} align="top" gutter={[8, 8]} key={field.key}>
          <Col flex={1}>
            <NewTaxCaseForm handleSourceDataChange={handleSourceDataChange}
              setSourceType={setSourceType}
              config={config.get(field.key)}
              setConfig={setConfig}
              activeKeys={activeKeys}
              setActiveKeys={setActiveKeys}
              form={form}
              debouncedFetchVin={debouncedFetchVin}
              setFirstRegDateDisabled={setFirstRegDateDisabled}
              field={field} />
          </Col>
          <Col flex={0}>
            <Button onClick={() => handleDelete(field)} className="muted delete-btn" type="text" shape="circle" icon={<Icon name="trash-outline" />} />
          </Col>
        </Row>
      ))}
      <Form.ErrorList errors={errors} />
      <Row className="mt-1" justify="space-between">
        <Col>
          <Button onClick={bulkCreateViaCSV}>Masseoprettelse med CSV-fil</Button>
        </Col>
        <Col>
          <Button type="link" onClick={addVehicle}>+ Tilføj køretøj</Button>
        </Col>
      </Row>
    </>
  )
}
type SourceType = "vin" | "link" | "motorAtlasId"
type NewTaxCaseFormProps = {
  form: FormInstance<NewTaxCaseFormType>;
  field: FormListFieldData
  activeKeys: Set<number>;
  setActiveKeys: React.Dispatch<React.SetStateAction<Set<number>>>
  config: FormListItemConfig | undefined;
  setSourceType: (sourceType: SourceType | null, field: FormListFieldData) => void
  setFirstRegDateDisabled: (firstRegDateDisabled: boolean, field: FormListFieldData) => void;
  debouncedFetchVin: DebouncedFunc<(vin: string, field: FormListFieldData) => void>
  setConfig: React.Dispatch<React.SetStateAction<Map<number, FormListItemConfig>>>
  handleSourceDataChange: (val: string | null | undefined, field: FormListFieldData) => void
}
function NewTaxCaseForm({ field, form, activeKeys, setActiveKeys, config, setConfig,
  handleSourceDataChange, debouncedFetchVin, setSourceType, setFirstRegDateDisabled
}: NewTaxCaseFormProps) {
  const { hasPermission } = UserStore.useContainer();
  const isAdmin = hasPermission("tax.isAdmin");
  // const idRef = useRef(nanoid())

  const sourceType = config?.sourceType ?? null;
  const vinVehicle = config?.vinVehicle ?? null;
  const linkVehicle = config?.linkVehicle ?? null;
  const loadingSourceData = config?.loadingSourceData ?? false
  const firstRegDateDisabled = config?.firstRegDateDisabled ?? false;
  const mileageDisabled = config?.mileageDisabled ?? false;

  const [selectedModel, setSelectedModel] = useState<ModelMatch | null>(null)
  const appliedFiltersRef = useRef<ModelCatalogFilterForm>()
  const headerTitle = formatVehiclePreview(sourceType, vinVehicle, linkVehicle, selectedModel)

  useEffect(() => {
    setConfig((prev) => {
      return new Map(prev).set(field.key, {
        linkVehicle: null,
        vinVehicle: null,
        sourceType: null,
        loadingSourceData: false,
        mileageDisabled: false,
        firstRegDateDisabled: false,
      })
    })
    return () => {
      setConfig((prev) => {
        const map = new Map(prev)
        map.delete(field.key)
        return map
      })
    }
  }, [])


  async function openModelCatalog() {
    try {
      const data = await openAsyncModal({
        views: {
          hideSourceVehicle: true,
        },
        selectedMotorAtlasId: selectedModel?.eCode ?? null,
        inputVehicle: null,
        presetFilters: appliedFiltersRef.current
      })
      if (data?.selectedMatch) {
        setSourceType("motorAtlasId", field)
        setSelectedModel(data.selectedMatch)
        setFirstRegDateDisabled(false, field)
        form.setFields([
          {
            name: ['vehicles', field.name, "motorAtlasId"],
            value: data.selectedMatch.eCode
          },
        ])
      }
      if (data?.appliedFilters) {
        appliedFiltersRef.current = data.appliedFilters
      }
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  return (
    <LoadingContainer showLogo loading={loadingSourceData}>
      <CustomCollapse onChange={(k) => {
        const [key] = k
        if (key) {
          setActiveKeys((prev) => new Set(prev).add(field.key))
        } else {
          setActiveKeys((prev) => {
            const s = new Set(prev)
            s.delete(field.key)
            return s;
          })
        }

      }}
        activeKey={activeKeys.has(field.key) ? field.key : []}
        className="ml-0 mr-0"
      >
        <Collapse.Panel forceRender key={field.key}
          header={
            <CustomCollapseHeader>
              <span><small>({field.name + 1})</small> {headerTitle}</span>
            </CustomCollapseHeader>
          }
        >
          <div className="mb-2" >
            {(sourceType === 'motorAtlasId' && selectedModel) ? (
              <>
                <div className="text-right mb-05">
                  <Button className="text-normal mr-05" onClick={() => setSelectedModel(null)} type="text">Fjern model</Button>
                  <Button className="pl-0 pr-0 text-normal" onClick={openModelCatalog} type="link">Åben modelkatalog</Button>
                </div>
                <SelectedModelPreview selectedModel={selectedModel} />
                <Form.Item
                  name={[field.name, "motorAtlasId"]}
                  rules={[{ required: true }]}
                  hidden
                >
                  <Input />
                </Form.Item>

              </>
            ) : (
              <div>
                <div className="text-right mb-05">
                  <Button className="pl-0 pr-0 text-normal" onClick={openModelCatalog} type="link">Vælg fra katalog</Button>
                </div>
                <Form.Item
                  wrapperCol={{ span: 24 }}
                  className="mb-05"
                  name={[field.name, "input"]}
                  rules={[{ required: true }]}
                >
                  <Input size="large" placeholder={_t("insert_vin_here", "insert_link", " eller ")}
                    onChange={(e) => handleSourceDataChange(e.target.value, field)}
                    prefix={<Icon name={
                      sourceType === 'link'
                        ? "link-outline"
                        : sourceType === 'vin'
                          ? "barcode-outline"
                          : "help-outline"
                    } />} />
                </Form.Item>
                <Form.Item
                  hidden
                  wrapperCol={{ span: 24 }}
                  className="mb-05"
                  name={[field.name, "link"]}
                >
                  <Input size="large"
                    placeholder={_t("insert_link")}
                    prefix={<Icon name={"link-outline"} />}
                  />
                </Form.Item>
                {linkVehicle && (
                  <LinkPreview vehicle={linkVehicle} />
                )}
                <Form.Item
                  hidden={!sourceType || sourceType !== 'link'}
                  wrapperCol={{ span: 24 }}
                  className="mb-05"
                  name={[field.name, "vin"]}
                >
                  <Input size="large"
                    placeholder={_t("insert_vin_here")}
                    onChange={(e) => debouncedFetchVin(e.target.value, field)}
                    prefix={<Icon name={"barcode-outline"} />}
                  />
                </Form.Item>
                {vinVehicle && (
                  <VinPreview vehicle={vinVehicle} />
                )}
              </div>
            )}
          </div>


          <Row wrap gutter={[16, 8]}>
            <Col flex={"0 0 200px"}>
              <Form.Item label={_t("firstRegDate")} shouldUpdate >
                <Row gutter={[8, 8]}>
                  <Col flex={"1 1 0"}>
                    <Form.Item noStyle
                      name={[field.name, "firstRegDate"]}
                      rules={[{ required: true }]}
                    >
                      <DateSelect style={{ minWidth: 0 }} disabled={firstRegDateDisabled} disabledDate={
                        (currentDate) => currentDate && currentDate > moment().endOf('day')
                      }
                      />
                    </Form.Item>
                  </Col>
                  <Col flex={"0 0 auto"}>
                    <Button type="text"
                      onClick={() => setFirstRegDateDisabled(!firstRegDateDisabled, field)}
                      icon={<Icon name="brush-outline" />} />
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col flex={"0 0 200px"}>
              <Form.Item name={[field.name, "mileage"]} label={_t("mileage")}>
                <InputNum disabled={mileageDisabled} />
              </Form.Item>
            </Col>
            <Col flex={"0 0 200px"}>
              <Form.Item
                className="mb-05"
                name={[field.name, "useCaseId"]}
                label={_t("special_use")}
                rules={[{ required: true }]}
              >
                <EnumSelect nameKey={["vehicle", "useCase"]} defaultActiveFirstOption />
              </Form.Item>
            </Col>
            <Col flex={"0 0 200px"}>
              <Form.Item
                name={[field.name, "evaluationTypeId"]}
                label={_t("evaluating_type")}
                rules={[{ required: true }]}
              >
                <EnumSelect nameKey={["taxCase", "evaluationType"]} />
              </Form.Item>
            </Col>
            <Col flex={"1 1 200px"}>
              <Form.Item name={[field.name, "note"]} label={_t("note")}>
                <Input.TextArea placeholder={_t("note_placeholder")} autoSize={{ minRows: 1, maxRows: 3 }} />
              </Form.Item>
            </Col>
            <Col flex={"0 0 240px"}>
              <Form.Item label=" " name={[field.name, "isPriority"]} valuePropName="checked">
                <Checkbox>{_t("high_priority_with_example")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name={[field.name, "files"]} label={_t("documents")} valuePropName="fileList" getValueFromEvent={normFile}>
                <DocumentsUpload
                  canLockFiles={isAdmin}
                  action={taxFileUrl()}
                  canDelete={!isAdmin}
                  multiple={true}
                />
              </Form.Item>
            </Col>
          </Row>
        </Collapse.Panel>
      </CustomCollapse>
    </LoadingContainer>
  )
}

function formatVehiclePreview(sourceType: SourceType | null, vinVehicle: RemoteVehicleData | null, linkVehicle: ScrapedVehicleType | null, selectedMatch: ModelMatch | null) {
  if (sourceType === 'motorAtlasId' && selectedMatch) {
    formatSelectedModelPreview(selectedMatch)
  }
  if (sourceType === 'link' && linkVehicle) {
    return formatLinkPreview(linkVehicle)
  }
  else if (sourceType === "vin" && vinVehicle) {
    return formatVinPreview(vinVehicle)
  } else {
    return _t("vehicle")
  }
}

function formatVinPreview(vehicle: RemoteVehicleData | null) {
  if (vehicle) {
    const dmrVehicle = vehicle.dmr
    const data = dmrVehicle?.mapped
    const numberPlates = dmrVehicle?.raw?.data?.registration
    return `${data?.brand} ${data?.model} ${(data?.equipmentVariant && data?.equipmentVariant !== "-") ? data?.equipmentVariant : ""}
      ${data?.horsePower ? data.horsePower : ""}
      ${numberPlates ? numberPlates : ""}
      ${data?.type ? data.type : ""}
    `
  }
  return null
}
function formatLinkPreview(vehicle: ScrapedVehicleType | null) {
  if (vehicle) {
    const data = vehicle.vehicle
    const type = vehicle.vehicle.type // Personbil vs Varebil
    return `${data.brand} ${data.model} ${(data?.equipmentVariant && data?.equipmentVariant !== "-") ? data?.equipmentVariant : ""}
      ${data.horsePower ? (
        `${data.horsePower} ${data.emissionAmount === 0 ? "KW" : "HP"}`
      ) : ""}
      ${type ? type : ""}
    `
  }

  return null
}

function formatSelectedModelPreview(model: ModelMatch) {
  return `${model.make} ${model.model} ${model.subModel} ${model.variant} ${model.horsePower}
  `
}

function SelectedModelPreview({ selectedModel }: { selectedModel: ModelMatch }) {
  const msg = formatSelectedModelPreview(selectedModel)
  return (
    <Alert type="info" className="mb-1" showIcon
      message={msg}
    />
  )
}

function VinPreview({ vehicle }: { vehicle: RemoteVehicleData | null }) {
  if (vehicle) {
    const msg = formatVinPreview(vehicle)
    return (
      <Alert type="info" className="mb-1" showIcon message={msg} />
    )
  }
  return null
}

function LinkPreview({ vehicle }: { vehicle: ScrapedVehicleType | null }) {
  if (vehicle) {
    const msg = formatLinkPreview(vehicle)
    return (
      <Alert type="info" className="mb-1" showIcon message={msg} />
    )
  }

  return null
}

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};


function ClipCost({ clipCost }: { clipCost: number; }) {
  const clips = getCreditCount()
  return (
    <Tag className="ant-tag-exception" color={clipCost > clips ? "error" : "blue"} >{clipCost}/{clips} klip</Tag>
  )
}
